/* index.js — the horary tool's page logic.
 *
 * ── The design decision that shapes everything here ──
 * natal-chart's funnel says 1714 opens → 1029 got past the place picker → 371
 * finished. Forty per cent of visitors are lost to data entry before the tool
 * has done anything for them. A horary needs no birth data at all: the moment
 * IS now and the place is where the asker is. So this page must open with the
 * chart already castable — place guessed, moment running — and ask the visitor
 * for exactly one thing: the question.
 *
 * The place guess comes from the browser's own time zone matched against the
 * shared city index, which needs no permission prompt and no round trip. It is
 * shown, not hidden, with one control to correct it, because the Ascendant
 * moves about a degree per four minutes of longitude and horary judgements can
 * turn on it.
 */

import { judge, rulerOfCusp } from './judge.js';
import { CATALOGUE, byId, groups, suggest, questionIssues, resolveHouse } from './questions.js';
import { renderVerdict, renderChain, renderDignities, renderHouses, renderCaveats, PLANET_GLYPH, esc, dm, pos } from './render.js';
import { renderWheel, standaloneWheelSvg, primeWheelCss } from '../natal/wheel.js';
import { findAspects } from '../natal/aspects.js';
import { pairOrb } from './orbs.js';
import { CLASSICAL } from './sky.js';

const $ = (id) => document.getElementById(id);
const CFG = window.__HORARY__ || {};
const T = CFG.i18n || {};
const t = (k) => (T[k] != null ? T[k] : k);
const LOCALE = window.__LOCALE || 'en';
const ae = (name, data) => { try { window.__ae && window.__ae(name, data || {}); } catch (e) { /* telemetry is never load-bearing */ } };

const state = {
    place: null,          // { lat, lng, tz, cityName, source }
    entry: null,          // catalogue record
    houseOverride: null,
    moment: null,         // ms — frozen when the question is asked
    result: null,
    userPickedType: false,
    missReported: false,
};

/* ── place ────────────────────────────────────────────────────────────────
 * Zone → representative city. The city index already carries an IANA zone and
 * a population for every row, so "the largest city in this zone" is a good
 * enough opening guess for every zone that has one, and it costs no permission
 * dialog. */

/* ⚠️ Row shape of /data/cities/index.json is
 *     [lat, lng, countryCode, tz, asciiName, population, geonameId, region]
 * which is NOT the shape documented in location-input.js — that comment
 * describes the RU supplement file (ru-extra.json), whose columns are in a
 * different order. Reading the supplement's layout into the main index put the
 * population where the time zone should be, so no city ever matched and the
 * place guess silently failed on every visit. Caught by the browser test, not
 * by anything in Node.
 * Localised names live in a parallel array, names-{locale}.json, indexed by
 * position — so "Moscow" becomes "Москва" without a second lookup table. */
const CITY_COL = { lat: 0, lng: 1, cc: 2, tz: 3, name: 4, pop: 5, gid: 6, region: 7 };

let cityIndex = null, cityNames = null;
async function loadCities() {
    if (cityIndex) return cityIndex;
    const v = window.__CITIES_V__ || '';
    const q = v ? '?v=' + v : '';
    try {
        const r = await fetch('/data/cities/index.json' + q, { credentials: 'omit' });
        const raw = await r.json();
        cityIndex = Array.isArray(raw) ? raw : (raw.index || raw.cities || []);
    } catch { cityIndex = []; }
    /* Names are a nicety: a failure here costs the Cyrillic label, not the guess. */
    try {
        const rn = await fetch(`/data/cities/names-${LOCALE}.json` + q, { credentials: 'omit' });
        if (rn.ok) {
            const nj = await rn.json();
            cityNames = Array.isArray(nj) ? nj : (nj.names || null);
        }
    } catch { cityNames = null; }
    return cityIndex;
}

async function guessPlace() {
    let tz = null;
    try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { /* ancient browser */ }

    /* A signed-in visitor may already have a saved location; it beats any guess. */
    const u = window.__USER_LOCATION;
    if (u && u.lat != null) {
        return { lat: u.lat, lng: u.lng, tz: u.tz || tz, cityName: u.cityName || null, source: 'account' };
    }
    if (!tz) return null;

    const rows = await loadCities();
    let best = null, bestIdx = -1;
    for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        if (!Array.isArray(row) || row[CITY_COL.tz] !== tz) continue;
        const popn = row[CITY_COL.pop] || 0;
        if (!best || popn > best.pop) {
            best = { lat: row[CITY_COL.lat], lng: row[CITY_COL.lng], pop: popn, cityName: row[CITY_COL.name] };
            bestIdx = i;
        }
    }
    if (!best) return null;
    if (cityNames && cityNames[bestIdx]) best.cityName = cityNames[bestIdx];
    return { lat: best.lat, lng: best.lng, tz, cityName: best.cityName, source: 'timezone' };
}

function paintPlace() {
    const el = $('hr-place-line');
    if (!el) return;
    if (!state.place) {
        el.innerHTML = `<span class="hr-warn">${esc(t('place_unknown'))}</span>`;
        return;
    }
    const p = state.place;
    const name = p.cityName || `${p.lat.toFixed(2)}, ${p.lng.toFixed(2)}`;
    el.textContent = name;
    el.title = t('place_source_' + p.source);
}

/** Строка момента рядом с городом: «сейчас» или выбранная дата. */
function paintMoment() {
    const el = $('hr-moment-line');
    if (!el) return;
    const mode = document.querySelector('input[name="hr-when"]:checked');
    if (!mode || mode.value === 'now') { el.textContent = t('moment_now_short'); return; }
    const d = $('hr-date'), tm = $('hr-time');
    el.textContent = (d && d.value ? d.value : '') + (tm && tm.value ? ' ' + tm.value : '') || t('moment_now_short');
}

/* ── the question catalogue ───────────────────────────────────────────────
 * Rendered as groups because nobody arrives thinking in house numbers. Search
 * is over the locale's keyword index, which is also what the free-text
 * suggester uses — one source, so a phrase that finds an entry by typing also
 * finds it by searching. */

function catalogueHTML() {
    const byGroup = {};
    for (const e of CATALOGUE) (byGroup[e.group] ||= []).push(e);
    return groups().map(g => `
        <div class="hr-cat-group">
            <h4>${esc(t('group_' + g))}</h4>
            <div class="hr-cat-items">
                ${byGroup[g].map(e => {
                    const h = resolveHouse(e);
                    return `<button type="button" class="hr-cat-item" data-id="${esc(e.id)}">
                        <span class="hr-cat-text">${esc(t('q_' + e.id))}</span>
                        <span class="hr-cat-house" title="${esc(t('col_house'))}">${h.house}</span>
                    </button>`;
                }).join('')}
            </div>
        </div>`).join('');
}

function paintChosen() {
    const el = $('hr-chosen');
    if (!el) return;
    if (!state.entry) { el.hidden = true; return; }
    const h = state.houseOverride
        ? { house: state.houseOverride, derived: false }
        : resolveHouse(state.entry);
    el.hidden = false;
    el.innerHTML = `
        <span class="hr-chosen-label">${esc(t('reading_as'))}</span>
        <strong>${esc(t('q_' + state.entry.id))}</strong>
        <span class="hr-chosen-house">${esc(t('house_n').replace(':n', String(h.house)))}${
            h.derived ? ` <span class="hr-dim">(${esc(t('derived_short').replace(':off', String(h.offset)).replace(':base', String(h.base)))})</span>` : ''
        }</span>
        <button type="button" class="hr-linkbtn" id="hr-change-type">${esc(t('change_type'))}</button>`;
    const btn = $('hr-change-type');
    if (btn) btn.addEventListener('click', () => openCatalogue());
}

function openCatalogue() {
    const box = $('hr-catalog');
    if (!box) return;
    box.hidden = false;
    const inp = $('hr-cat-search');
    if (inp) inp.focus();
    ae('feature_use', { f: 'catalogue_open' });
}

function chooseEntry(id, how) {
    state.entry = byId(id);
    state.houseOverride = null;
    /* Ручной выбор старше автоматического: дальше печатание текста не должно
     * переопределять то, что человек выбрал руками. */
    if (how !== 'auto') state.userPickedType = true;
    const box = $('hr-catalog');
    if (box) box.hidden = true;
    paintChosen();
    /* `how` различает, откуда пришёл выбор: 'auto' — распознавание, 'suggest' —
     * человек согласился с подсказкой, 'catalogue'/'starter' — выбрал сам. Смена
     * ПОСЛЕ автоматического выбора означает, что распознавание ошиблось, и это
     * единственный способ узнать, на каких формулировках. */
    ae('feature_use', { f: 'question_type_chosen', how, id, afterAuto: !!state.autoPicked });
    if (how === 'auto') state.autoPicked = id; else if (state.autoPicked && state.autoPicked !== id) {
        ae('feature_use', { f: 'auto_corrected', from: state.autoPicked, to: id });
    }
}

/* Шесть вопросов, которые задают чаще всего. Показываются, пока поле пустое:
 * человек, впервые попавший на страницу, видит не пустой прямоугольник, а то,
 * о чём тут вообще спрашивают, и может начать одним нажатием. */
const STARTERS = ['money_loan_repaid', 'job_will_i_get_it', 'love_will_they_return',
    'lost_my_object', 'people_will_they_reply', 'decide_should_i_do_it'];

function paintStarters() {
    const box = $('hr-starters');
    if (!box) return;
    const q = ($('hr-question') || {}).value || '';
    if (q.trim().length > 1 || state.entry) { box.hidden = true; return; }
    box.hidden = false;
    box.innerHTML = STARTERS.map(id => byId(id)
        ? `<button type="button" class="hr-starter" data-id="${esc(id)}">${esc(t('q_' + id))}</button>`
        : '').join('');
}

/* Подсказки по свободному тексту.
 *
 * ⚠️ Автовыбор здесь появился НЕ от лени: замер показал, что человек, который
 * ввёл вопрос и нажал кнопку, получал тост «выберите тип» вместо ответа —
 * лишний шаг ровно там, где он уже сказал всё, что нужно. Поэтому уверенный
 * лидер применяется САМ, но никогда молча: выбранный тип и его дом печатаются
 * над кнопкой, рядом стоит «другой тип», и это ровно то предупреждение, которого
 * требует правило «молча применённый неверный дом — худший провал этого тула».
 * Условие уверенности — заметный отрыв от второго кандидата. */
let suggestTimer = null;
function onQuestionInput() {
    clearTimeout(suggestTimer);
    suggestTimer = setTimeout(() => {
        const text = ($('hr-question') || {}).value || '';
        const box = $('hr-suggest');
        paintStarters();
        if (!box) return;
        const hits = suggest(text, CFG.keywords || {}, 4);
        if (!hits.length) {
            box.hidden = true;
            /* ⚠️ Главный сигнал качества этого тула: человек написал осмысленный
             * вопрос, а каталог из 61 ситуации не нашёл ничего. Без этого события
             * «умность» распознавания невозможно ни подтвердить, ни опровергнуть —
             * видно было бы только то, сколько раз оно сработало, но не сколько
             * раз промахнулось. Текст НЕ отправляется, только его длина и число
             * слов: тема вопроса — личное дело человека. */
            if (text.trim().length >= 12 && !state.missReported) {
                state.missReported = true;
                ae('feature_use', { f: 'suggest_miss', len: text.trim().length,
                    words: text.trim().split(/\s+/).length });
            }
            return;
        }
        state.missReported = false;

        const confident = hits.length === 1 || (hits[0].score >= (hits[1].score || 0) * 1.6);
        if (confident && !state.userPickedType) {
            state.entry = byId(hits[0].id);
            state.houseOverride = null;
            paintChosen();
            ae('feature_use', { f: 'type_auto', id: hits[0].id, score: hits[0].score });
        }

        box.hidden = false;
        box.innerHTML = `<span class="hr-suggest-label">${esc(t('suggest_label'))}</span>` +
            hits.map(h => `<button type="button" class="hr-suggest-item${state.entry && state.entry.id === h.id ? ' is-on' : ''}" data-id="${esc(h.id)}">
                ${esc(t('q_' + h.id))}</button>`).join('');
    }, 220);
}

/* ── running the judgement ───────────────────────────────────────────────── */

function currentMoment() {
    const mode = document.querySelector('input[name="hr-when"]:checked');
    if (mode && mode.value === 'custom') {
        const d = $('hr-date'), tm = $('hr-time');
        if (d && d.value) {
            /* Interpreted in the PLACE's zone, not the browser's: a person
             * asking about a moment in another city must get that city's chart.
             * Falls back to the browser zone when the guess has no IANA name. */
            return zonedToUtc(d.value, tm && tm.value ? tm.value : '12:00', state.place && state.place.tz);
        }
    }
    return Date.now();
}

/** "2026-09-05","14:30" + IANA zone → epoch ms. */
function zonedToUtc(dateStr, timeStr, tz) {
    const naive = new Date(`${dateStr}T${timeStr}:00Z`).getTime();
    if (!tz) return new Date(`${dateStr}T${timeStr}:00`).getTime();
    /* Two passes: the offset itself depends on the instant (DST), so apply it
     * and re-measure. Converges for every real zone. */
    let guess = naive;
    for (let i = 0; i < 2; i++) {
        const off = zoneOffsetMs(guess, tz);
        guess = naive - off;
    }
    return guess;
}

function zoneOffsetMs(ms, tz) {
    try {
        const dtf = new Intl.DateTimeFormat('en-US', {
            timeZone: tz, hour12: false,
            year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit', second: '2-digit',
        });
        const p = {};
        for (const { type, value } of dtf.formatToParts(new Date(ms))) p[type] = value;
        const asUTC = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour % 24, +p.minute, +p.second);
        return asUTC - ms;
    } catch { return 0; }
}

function run() {
    const qEl = $('hr-question');
    const text = (qEl && qEl.value || '').trim();

    if (!state.place) {
        window.__toast && window.__toast(t('need_place'), 'warning');
        return;
    }
    if (!state.entry) {
        window.__toast && window.__toast(t('need_type'), 'warning');
        openCatalogue();
        return;
    }
    const issues = questionIssues(text);
    const blocking = issues.find(i => i.severity === 'block');
    if (blocking) {
        window.__toast && window.__toast(t('issue_' + blocking.key), 'warning');
        qEl && qEl.focus();
        return;
    }

    /* The moment is frozen HERE — at the press, not at render time — because
     * that is what horary means by the moment of the question, and because a
     * moment that drifts while the page renders is not reproducible. */
    state.moment = currentMoment();

    const t0 = performance.now();
    let r;
    try {
        r = judge({
            t: state.moment,
            lat: state.place.lat, lon: state.place.lng,
            entry: state.entry,
            overrides: state.houseOverride ? { house: state.houseOverride } : {},
        });
    } catch (err) {
        window.__handleError && window.__handleError(err, 'horary/judge');
        ae('tool_error', { msg: String(err && err.message || err).slice(0, 120) });
        return;
    }
    const ms = performance.now() - t0;
    state.result = r;
    /* Exposed so an automated run can re-judge the SAME moment headlessly and
     * compare with what is on screen. Without it a browser test can only prove
     * that something rendered, not that the right thing rendered. */
    window.__HORARY_LAST__ = { moment: r.moment, lean: r.verdict.lean, house: r.significators.quesitedHouse };

    paintResult(r, text, issues);
    ae('tool_complete', {
        lean: r.verdict.lean, house: r.significators.quesitedHouse,
        entry: state.entry.id, ms: Math.round(ms),
        route: r.verdict.route ? r.verdict.route.kind : 'none',
        cautions: r.considerations.summary.cautionCount,
    });
    window.__stage && window.__stage('judged');
}

function paintResult(r, text, issues) {
    const box = $('hr-results');
    if (!box) return;
    const opts = { locale: LOCALE, tz: state.place && state.place.tz };

    const advisories = issues.filter(i => i.severity !== 'block');
    const advHTML = advisories.length
        ? `<div class="hr-advisories">${advisories.map(i => `<p>${esc(t('issue_' + i.key))}</p>`).join('')}</div>`
        : '';

    /* Напоминание о моменте перфекции — единственная механика в этой нише,
     * которая связывает вычисленную минуту с письмом. Кнопка показывается ВСЕМ
     * (перк, которого не видно, ничего не продаёт), но не-Премиуму она ведёт на
     * предложение, а не на сохранение. Порог в 6 часов — потому что напоминать
     * о моменте, который человек только что видел на экране, незачем. */
    const canRemind = r.timing && r.timing.mundane
        && (r.timing.mundane.at - Date.now()) > 6 * 3600 * 1000;
    const isPro = typeof window.__isProNow === 'function' ? window.__isProNow(false) : false;
    const remindHtml = canRemind
        ? `<button type="button" class="hr-btn hr-btn-ghost${isPro ? '' : ' hr-locked'}" id="hr-remind">
               ${isPro ? '' : '<span class="hr-lock" aria-hidden="true">🔒</span> '}${esc(t('remind_me'))}
           </button>`
        : '';

    box.innerHTML = `
        <div class="hr-result-head">
            <p class="hr-asked">${esc(t('asked_at'))} <strong>${esc(fmtMoment(r.moment))}</strong>
               · ${esc(state.place.cityName || `${state.place.lat.toFixed(2)}, ${state.place.lng.toFixed(2)}`)}</p>
            ${text ? `<blockquote class="hr-qtext">${esc(text)}</blockquote>` : ''}
        </div>
        ${advHTML}
        ${renderCaveats(r, t)}
        ${renderVerdict(r, t, opts)}
        <section class="hr-panel">
            <h3>${esc(t('chain_title'))}</h3>
            <p class="hr-dim">${esc(t('chain_lead'))}</p>
            ${renderChain(r, t, opts)}
        </section>
        <section class="hr-panel">
            <h3>${esc(t('wheel_title'))}</h3>
            <div class="hr-wheel" id="hr-wheel"></div>
            <p class="hr-dim">${esc(t('wheel_note'))}</p>
        </section>
        <section class="hr-panel">
            <h3>${esc(t('dignities_title'))}</h3>
            ${renderDignities(r, t)}
        </section>
        <section class="hr-panel">
            <h3>${esc(t('houses_title'))}</h3>
            ${renderHouses(r, t, rulerOfCusp)}
        </section>
        <div class="hr-actions">
            <button type="button" class="hr-btn" id="hr-save">${esc(t('save_to_journal'))}</button>
            ${remindHtml}
            <button type="button" class="hr-btn hr-btn-ghost" id="hr-copy">${esc(t('copy_report'))}</button>
            <button type="button" class="hr-btn hr-btn-ghost" id="hr-svg">${esc(t('download_wheel'))}</button>
        </div>`;

    drawWheel(r);
    const again = $('hr-again');
    if (again) again.hidden = false;

    /* Раскрытие «то же на языке традиции» и шагов разбора: это и есть мера
     * того, читают ли люди объяснение или им хватает короткого ответа. */
    for (const d of box.querySelectorAll('details')) {
        d.addEventListener('toggle', () => {
            if (!d.open) return;
            const kind = d.classList.contains('hr-verdict-details') ? 'verdict_terms'
                : (d.querySelector('.hr-step-title') || {}).textContent || 'step';
            ae('feature_use', { f: 'detail_open', kind: String(kind).slice(0, 24) });
        }, { once: true });
    }
    const s = $('hr-save'); if (s) s.addEventListener('click', () => saveToJournal(text));
    const c = $('hr-copy'); if (c) c.addEventListener('click', () => copyReport(r, text));
    const d = $('hr-svg'); if (d) d.addEventListener('click', () => downloadWheel(r));
    const rem = $('hr-remind'); if (rem) rem.addEventListener('click', () => saveReminder(r, text, rem));
}

function fmtMoment(ms) {
    try {
        return new Intl.DateTimeFormat(LOCALE, {
            dateStyle: 'medium', timeStyle: 'short',
            timeZone: (state.place && state.place.tz) || undefined,
        }).format(new Date(ms));
    } catch { return new Date(ms).toISOString().slice(0, 16).replace('T', ' '); }
}

/** Short date for a journal row. Separate from fmtMoment: the journal lists
 *  many rows and a full timestamp on each is noise. */
function fmtDate(ms) {
    try {
        return new Intl.DateTimeFormat(LOCALE, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })
            .format(new Date(ms));
    } catch { return new Date(ms).toISOString().slice(0, 16).replace('T', ' '); }
}

function drawWheel(r) {
    const host = $('hr-wheel');
    if (!host) return;
    fitWheelBleed();
    const points = {};
    for (const k of CLASSICAL) points[k] = { lon: r.chart.sky[k].lon, speed: r.chart.sky[k].speed, retrograde: r.chart.sky[k].retrograde };

    /* Aspects for the wheel use the TRADITIONAL pair orbs, not the natal
     * module's per-aspect table — otherwise the picture would disagree with
     * the judgement sitting above it. */
    const asp = [];
    const keys = Object.keys(points);
    for (let i = 0; i < keys.length; i++) {
        for (let j2 = i + 1; j2 < keys.length; j2++) {
            const a = keys[i], b = keys[j2];
            const sep = Math.abs(((points[a].lon - points[b].lon) % 360 + 540) % 360 - 180);
            for (const [name, angle] of Object.entries({ conjunction: 0, sextile: 60, square: 90, trine: 120, opposition: 180 })) {
                if (Math.abs(sep - angle) <= pairOrb(a, b)) {
                    asp.push({ a, b, type: name, name, major: true, orb: Math.abs(sep - angle) });
                }
            }
        }
    }

    const sig = new Set([r.significators.querent, r.significators.quesited, 'moon']);
    host.innerHTML = renderWheel(
        { angles: { asc: r.chart.asc, mc: r.chart.mc, dsc: r.chart.dsc, ic: r.chart.ic }, cusps: r.chart.cusps, points, aspects: asp },
        {
            size: 640,
            bodies: CLASSICAL,
            aspectBodies: CLASSICAL,
            labelOf: (k) => (sig.has(k) ? '•' : null),
            title: t('wheel_title'),
            desc: t('wheel_desc'),
        }
    );
}

/* ── journal ──────────────────────────────────────────────────────────────
 * Horary's one testable promise: the moment cannot be chosen after the fact.
 * A journal that records the question, the chart and the verdict BEFORE the
 * outcome is known — and then lets the asker mark what actually happened — is
 * the only honest way anyone can find out whether this works for them. No
 * competitor offers it outside a paid tier, and it costs us nothing: it is
 * localStorage.
 */

const JOURNAL_KEY = 'horary:journal:v1';

function readJournal() {
    try { return JSON.parse(localStorage.getItem(JOURNAL_KEY) || '[]'); } catch { return []; }
}
function writeJournal(rows) {
    try { localStorage.setItem(JOURNAL_KEY, JSON.stringify(rows.slice(0, 200))); return true; } catch { return false; }
}

function saveToJournal(text) {
    const r = state.result;
    if (!r) return;
    const rows = readJournal();
    rows.unshift({
        id: 'h' + r.moment.toString(36) + Math.random().toString(36).slice(2, 6),
        at: r.moment,
        q: text.slice(0, 300),
        entry: state.entry && state.entry.id,
        house: r.significators.quesitedHouse,
        lean: r.verdict.lean,
        reasons: r.verdict.reasons,
        place: { lat: state.place.lat, lng: state.place.lng, tz: state.place.tz, name: state.place.cityName },
        perfectsAt: r.timing ? r.timing.mundane.at : null,
        outcome: null,
    });
    if (writeJournal(rows)) {
        window.__toast && window.__toast(t('saved'), 'success');
        paintJournal();
        ae('feature_use', { f: 'journal_save' });
    } else {
        window.__toast && window.__toast(t('save_failed'), 'error');
    }
}

function paintJournal() {
    const box = $('hr-journal');
    if (!box) return;
    const rows = readJournal();
    if (!rows.length) { box.innerHTML = `<p class="hr-dim">${esc(t('journal_empty'))}</p>`; return; }

    const done = rows.filter(r => r.outcome === 'yes' || r.outcome === 'no');
    const agreed = done.filter(r => (r.outcome === 'yes') === (r.lean === 'yes' || r.lean === 'yes_with')).length;
    const tally = done.length
        ? `<p class="hr-journal-tally">${esc(t('journal_tally')
            .replace(':n', String(done.length)).replace(':k', String(agreed)))}</p>`
        : `<p class="hr-dim">${esc(t('journal_tally_none'))}</p>`;

    box.innerHTML = tally + `<ul class="hr-journal-list">` + rows.slice(0, 40).map(r => `
        <li data-id="${esc(r.id)}">
            <div class="hr-j-top">
                <span class="hr-j-verdict hr-tone-${r.lean === 'no' ? 'no' : r.lean === 'no_answer' ? 'unclear' : r.lean === 'yes' ? 'yes' : 'mixed'}">${esc(t('verdict_' + r.lean))}</span>
                <span class="hr-j-when">${esc(fmtDate(r.at))}</span>
            </div>
            <p class="hr-j-q">${esc(r.q || t('q_' + r.entry))}</p>
            <div class="hr-j-outcome">
                <span class="hr-dim">${esc(t('what_happened'))}</span>
                ${['yes', 'no', 'unknown'].map(o => `
                    <button type="button" class="hr-j-btn${r.outcome === o ? ' is-on' : ''}" data-o="${o}">${esc(t('outcome_' + o))}</button>`).join('')}
                <button type="button" class="hr-j-del" data-del="1" aria-label="${esc(t('delete'))}">×</button>
            </div>
        </li>`).join('') + '</ul>';
}

function onJournalClick(e) {
    const li = e.target.closest('li[data-id]');
    if (!li) return;
    const rows = readJournal();
    const row = rows.find(r => r.id === li.dataset.id);
    if (!row) return;
    if (e.target.dataset.del) {
        writeJournal(rows.filter(r => r.id !== row.id));
        paintJournal();
        return;
    }
    const o = e.target.dataset.o;
    if (o) {
        row.outcome = row.outcome === o ? null : o;
        writeJournal(rows);
        paintJournal();
        ae('feature_use', { f: 'journal_outcome', o, lean: row.lean });
    }
}

/* ── напоминание о моменте перфекции ──────────────────────────────────────
 * Премиум на обоих концах: здесь — чтобы не обещать того, чего не будет, и на
 * сервере — потому что клиентская проверка не защита. Отказ 403 приходит с
 * кодом `premium_required`, и по нему рисуется предложение, а не безликая
 * ошибка. */
async function saveReminder(r, question, btn) {
    if (!r.timing || !r.timing.mundane) return;

    const pro = typeof window.__isProNow === 'function' ? window.__isProNow(false) : false;
    if (!pro) {
        ae('feature_use', { f: 'remind_upsell', lean: r.verdict.lean });
        window.__toast && window.__toast(t('remind_premium'), 'info');
        if (window.__PRO_URL) window.open(window.__PRO_URL, '_blank', 'noopener');
        return;
    }

    btn.disabled = true;
    try {
        const res = await fetch('/api/horary-reminder', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
                'X-CSRF-TOKEN': (document.querySelector('meta[name="csrf-token"]') || {}).content || '',
            },
            body: JSON.stringify({
                question: (question || t('q_' + (state.entry && state.entry.id))).slice(0, 300),
                entry_id: state.entry && state.entry.id,
                house: r.significators.quesitedHouse,
                asked_at: r.moment,
                remind_at: r.timing.mundane.at,
                lean: r.verdict.lean,
                place_name: state.place && state.place.cityName,
                lat: state.place && state.place.lat,
                lng: state.place && state.place.lng,
            }),
        });
        const data = await res.json().catch(() => ({}));
        if (res.ok) {
            btn.textContent = t('remind_set');
            ae('feature_use', { f: 'remind_set', days: Math.round((r.timing.mundane.at - Date.now()) / 86400000) });
            return;
        }
        if (data.error === 'premium_required') {
            window.__toast && window.__toast(t('remind_premium'), 'info');
            if (window.__PRO_URL) window.open(window.__PRO_URL, '_blank', 'noopener');
        } else if (data.error === 'auth_required') {
            window.__toast && window.__toast(t('remind_login'), 'info');
        } else {
            window.__toast && window.__toast(t('remind_failed'), 'error');
        }
        ae('feature_use', { f: 'remind_refused', why: String(data.error || res.status).slice(0, 24) });
    } catch (e) {
        window.__toast && window.__toast(t('remind_failed'), 'error');
    } finally {
        btn.disabled = false;
    }
}

/* ── export ───────────────────────────────────────────────────────────────── */

function reportText(r, question) {
    const L = [];
    L.push(`${t('report_title')}`);
    L.push(`${t('asked_at')} ${fmtMoment(r.moment)} · ${state.place.cityName || ''}`);
    if (question) L.push(`${t('the_question')}: ${question}`);
    L.push('');
    L.push(`${t('verdict_' + r.verdict.lean)} — ${r.verdict.reasons.map(x => t('reason_' + x)).join('; ')}`);
    L.push('');
    L.push(`${t('the_querent')}: ${t('planet_' + r.significators.querent)}`);
    L.push(`${t('the_quesited')}: ${t('planet_' + r.significators.quesited)} (${t('house_n').replace(':n', r.significators.quesitedHouse)})`);
    L.push('');
    for (const k of CLASSICAL) {
        const c = r.condition[k];
        L.push(`${t('planet_' + k)}  ${dm(c.deg)} ${t('sign_' + ['aries', 'taurus', 'gemini', 'cancer', 'leo', 'virgo', 'libra', 'scorpio', 'sagittarius', 'capricorn', 'aquarius', 'pisces'][c.sign])}  ${t('house_n').replace(':n', c.house)}  ess ${c.essential.score >= 0 ? '+' : ''}${c.essential.score}  acc ${c.accidental.score >= 0 ? '+' : ''}${c.accidental.score}${c.retrograde ? '  R' : ''}`);
    }
    if (r.timing) L.push('', `${t('timing_mundane')}: ${new Date(r.timing.mundane.at).toISOString().slice(0, 16).replace('T', ' ')}`);
    L.push('', CFG.origin || '');
    return L.join('\n');
}

function copyReport(r, question) {
    const txt = reportText(r, question);
    const done = () => { window.__toast && window.__toast(t('copied'), 'success'); ae('feature_use', { f: 'copy_report' }); };
    if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(txt).then(done).catch(() => fallbackCopy(txt, done));
    } else fallbackCopy(txt, done);
}

function fallbackCopy(txt, done) {
    const ta = document.createElement('textarea');
    ta.value = txt; ta.setAttribute('readonly', ''); ta.style.position = 'fixed'; ta.style.opacity = '0';
    document.body.appendChild(ta); ta.select();
    try { document.execCommand('copy'); done(); } catch { window.__toast && window.__toast(t('copy_failed'), 'error'); }
    document.body.removeChild(ta);
}

/**
 * Скачивание колеса.
 *
 * ⚠️⚠️ Здесь стоял `svg.outerHTML` прямо в Blob — и это отдавало ПОЛНОСТЬЮ
 * ЧЁРНЫЙ файл: колесо целиком красят `.nc-*` из внешнего natal-wheel.css, а в
 * самостоятельном документе этих правил нет, и каждая фигура падает на
 * `fill:black`. Замер 06.09.2026: доля чистого чёрного 1.000, различимых
 * цветов в файле ОДИН. На экране при этом всё было в порядке, поэтому дефект
 * не был виден ни глазом, ни сторожем.
 *
 * Сборка портативной копии живёт в общем модуле колеса — там же, где колесо
 * рисуется. Второй копии этой логики быть не должно: она уже разъезжалась.
 */
/**
 * Вынос колеса за поля панели на узком экране — считается ЗАМЕРОМ, а не
 * константой: между колесом и краем экрана лежат два отступа, и внешний нам не
 * принадлежит.
 */
function fitWheelBleed() {
    const box = $('hr-wheel');
    if (!box) return;
    box.style.removeProperty('--hr-bleed');
    if (!window.matchMedia('(max-width: 560px)').matches) return;
    const bleed = Math.floor((document.documentElement.clientWidth - box.getBoundingClientRect().width) / 2);
    if (bleed > 0) box.style.setProperty('--hr-bleed', bleed + 'px');
}

let hrBleedTimer = 0;
window.addEventListener('resize', () => {
    clearTimeout(hrBleedTimer);
    hrBleedTimer = setTimeout(fitWheelBleed, 120);
});

function downloadWheel(r) {
    const host = $('hr-wheel');
    const svg = host && host.querySelector('svg');
    if (!svg) return;
    const text = standaloneWheelSvg(svg);
    if (!text) {
        /* Отказ лучше выдачи: чёрный диск человек увидит уже после сохранения. */
        ae('feature_use', { f: 'download_wheel_css_missing' });
        window.__toast && window.__toast(t('copy_failed'), 'error');
        return;
    }
    const blob = new Blob([text], { type: 'image/svg+xml' });
    const name = `horary-${new Date(r.moment).toISOString().slice(0, 10)}.svg`;
    if (window.__downloadFile) window.__downloadFile(blob, name);
    ae('feature_use', { f: 'download_wheel' });
}

/* ── boot ─────────────────────────────────────────────────────────────────── */

async function boot() {
    /* Стили колеса греются заранее: экспорт обязан оставаться синхронным —
       загрузка внутри пользовательского жеста тратит его активацию. */
    primeWheelCss();
    /* Place first, so the page is usable the instant it paints. */
    state.place = await guessPlace();
    paintPlace();
    if (!state.place) ae('feature_use', { f: 'place_guess_failed' });

    const cat = $('hr-catalog-body');
    if (cat) cat.innerHTML = catalogueHTML();
    paintStarters();
    paintMoment();

    document.addEventListener('click', (e) => {
        const item = e.target.closest('.hr-cat-item');
        if (item) { chooseEntry(item.dataset.id, 'catalogue'); return; }
        const st = e.target.closest('.hr-starter');
        if (st) {
            const qEl = $('hr-question');
            if (qEl && !qEl.value.trim()) qEl.value = t('q_' + st.dataset.id);
            chooseEntry(st.dataset.id, 'starter');
            paintStarters();
            return;
        }
        const sug = e.target.closest('.hr-suggest-item');
        if (sug) {
            chooseEntry(sug.dataset.id, 'suggest');
            const box = $('hr-suggest'); if (box) box.hidden = true;
            return;
        }
    });

    const q = $('hr-question');
    if (q) q.addEventListener('input', onQuestionInput);

    const search = $('hr-cat-search');
    if (search) {
        search.addEventListener('input', () => {
            const v = search.value.toLowerCase().trim();
            for (const el of document.querySelectorAll('.hr-cat-item')) {
                const txt = el.textContent.toLowerCase();
                el.hidden = v.length > 1 && !txt.includes(v);
            }
            for (const g of document.querySelectorAll('.hr-cat-group')) {
                g.hidden = !g.querySelector('.hr-cat-item:not([hidden])');
            }
        });
    }

    const runBtn = $('hr-run');
    if (runBtn) runBtn.addEventListener('click', run);

    const openBtn = $('hr-open-catalog');
    if (openBtn) openBtn.addEventListener('click', openCatalogue);

    const closeBtn = $('hr-cat-close');
    if (closeBtn) closeBtn.addEventListener('click', () => { const b = $('hr-catalog'); if (b) b.hidden = true; });

    for (const el of document.querySelectorAll('input[name="hr-when"]')) {
        el.addEventListener('change', () => {
            const custom = document.querySelector('input[name="hr-when"]:checked').value === 'custom';
            const row = $('hr-when-custom');
            if (row) row.hidden = !custom;
            paintMoment();
            if (custom) {
                const d = $('hr-date'), tm = $('hr-time');
                const now = new Date();
                if (d && !d.value) d.value = window.__localYmd(now);
                if (tm && !tm.value) tm.value = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(now);
            }
            ae('feature_use', { f: 'moment_mode', custom });
        });
    }

    for (const id of ['hr-date', 'hr-time']) {
        const el = $(id);
        if (el) el.addEventListener('change', paintMoment);
    }

    /* Следующий вопрос из блока в конце ответа: тот же путь, что у формы
     * наверху — текст уходит в основное поле, распознавание типа отрабатывает,
     * потом run(). Слишком короткий вопрос отклоняется ЗДЕСЬ, с фокусом здесь:
     * run() поставил бы фокус в форму наверху страницы, и из конца ответа это
     * прыжок прочь от того места, где человек печатает. */
    const quickGo = $('hr-quick-go'), quickQ = $('hr-quick-q');
    const runFromQuick = () => {
        const qEl = $('hr-question');
        if (!qEl || !quickQ) return;
        const blocking = questionIssues(quickQ.value).find(i => i.severity === 'block');
        if (blocking) {
            window.__toast && window.__toast(t('issue_' + blocking.key), 'warning');
            quickQ.focus();
            return;
        }
        qEl.value = quickQ.value;
        onQuestionInput();
        /* Дать распознаванию отработать (оно с задержкой), потом судить. */
        setTimeout(() => {
            ae('feature_use', { f: 'quickbar_run' });
            run();
        }, 300);
    };
    if (quickGo) quickGo.addEventListener('click', runFromQuick);
    if (quickQ) quickQ.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); runFromQuick(); } });

    const jbox = $('hr-journal');
    if (jbox) jbox.addEventListener('click', onJournalClick);
    paintJournal();

    /* The shared city picker, mounted lazily: most visitors accept the guess,
     * and the dataset is worth several hundred kilobytes. */
    const ctxToggle = $('hr-context-toggle');
    if (ctxToggle) {
        ctxToggle.addEventListener('click', () => {
            const panel = $('hr-context-panel');
            if (!panel) return;
            panel.hidden = !panel.hidden;
            ae('feature_use', { f: 'context_panel', open: !panel.hidden });
            if (panel.hidden) return;
            const host = $('hr-place-picker');
            if (!host) return;
            if (host.dataset.mounted) return;
            host.dataset.mounted = '1';
            if (!window.LocationInput) { window.__toast && window.__toast(t('picker_unavailable'), 'error'); return; }
            window.LocationInput.mount(host, {
                tool: 'horary',
                initial: state.place ? { lat: state.place.lat, lng: state.place.lng, cityName: state.place.cityName, tz: state.place.tz } : {},
                t: CFG.locationStrings || {},
                onChange: (p) => {
                    state.place = { lat: p.lat, lng: p.lng, tz: p.tz || (state.place && state.place.tz), cityName: p.cityName, source: p.source };
                    paintPlace();
                    ae('feature_use', { f: 'place_changed', source: p.source });
                },
            });
        });
    }

    window.__stage && window.__stage('ready');
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();

export { state, guessPlace, zonedToUtc };
